import { useCallback, useEffect, useMemo, useState } from 'react';
import Cookies from 'js-cookie';
import get from 'lodash/get';
import lowerCase from 'lodash/lowerCase';
import { useHistory } from 'react-router-dom';
import { getFilteredProductsBloomreach } from '@yme/api';
import { QueryParams, VARIANTS_PRICES_DISCOUNT } from '@yme/common/constants';
import { PRODUCT_FL_PARAMS } from '@yme/components/SearchPage/hooks/constants';
import { useSelector } from '@yme/hooks/useSelector';
import { getBloomreachModel, getNavigationBreadCrumbs } from '@yme/selectors/common';
import { getIsInAuthor } from '@yme/selectors/index';
import { useTranslation } from '@yme/services/i18n';
import { BR_ID_COOKIE, FacetFields, PixelEvent, PixelPageView, RequestType, SearchType, setPixelEvent, setPixelPageView, } from '@yme/utils/bloomreach';
import { getFacetsData, getFacetsValues, getParamsFromURL, getSelectedFacetsFromUrl, mergeProductFilterFacetsCounts, } from '../common/helpers';
import { useSortFilterOptions } from '../common/useSortFilterOptions';
import { MAX_LIMIT_PER_REQUEST } from './constants';
import { getCurrentFacets, getFacetProperties, getHasDiscountedProducts, getPillsOptions, } from './helpers';
export function useProductFilter(props) {
    const { categoryId = '', filterFacetsConfig, productType = '', compatiblePanelAPI, sortOptionsType, isDiscountMode, isCMPPage, loadedItemsOffset = 0, accessoryTypeTranslations = [], accessoryUsageTranslations, genderTranslations, subcategoryTranslations, } = props;
    const isInAuthor = useSelector(getIsInAuthor);
    const { searchHost, accountId, productCatalog } = useSelector(getBloomreachModel);
    const navigationBreadCrumbs = useSelector(getNavigationBreadCrumbs);
    const [offset, setOffset] = useState(loadedItemsOffset);
    const compatibleProductKey = get(compatiblePanelAPI, 'compatibleProduct.key', null);
    const history = useHistory();
    const queryParams = new URLSearchParams(history.location.search);
    const isDiscountFromAEM = isDiscountMode || (isCMPPage && queryParams.get(QueryParams.DISCOUNTED_MODE));
    const { t, dictionary } = useTranslation();
    const [autoCorrectQueryResult, setAutoCorrectQueryResult] = useState('');
    const [didYouMeanOptions, setDidYouMeanOptions] = useState([]);
    const [resultsData, setResultsData] = useState([]);
    const [isDataExists, setIsDataExists] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    const [facets, setFacets] = useState([]);
    const [isFacetsLoaded, setIsFacetsLoaded] = useState(false);
    const [hasDiscountedProducts, setHasDiscountedProducts] = useState(false);
    const facetsConfig = useMemo(() => filterFacetsConfig.map((id) => id), [filterFacetsConfig]);
    const { searchParam, sortOrderParam, facetsParams, isDiscountedParam, typeEnParam } = useMemo(() => {
        const url = history.location.search;
        return getParamsFromURL(url, facetsConfig);
    }, [facetsConfig, history.location.search]);
    const [isShowOnlyDiscounted, setIsShowOnlyDiscounted] = useState(Boolean(isDiscountedParam) || Boolean(isDiscountFromAEM));
    // additional options
    const [isFilterCleared, setIsFilterCleared] = useState(true);
    // for searching
    const [search, setSearch] = useState(searchParam ?? '');
    // sorting functionality
    const { sortFilterOptions } = useSortFilterOptions({
        type: sortOptionsType,
    });
    const defaultSortOption = sortFilterOptions.find((option) => Boolean(option?.isValueDefault)) || sortFilterOptions[0];
    const sortOrderValue = sortOrderParam ?? defaultSortOption.value;
    const [sortOrder, setSortOrder] = useState(sortOrderValue);
    const [total, setTotal] = useState(0);
    const selectedFacets = useMemo(() => {
        if (facets?.length) {
            return facets
                .filter((facet) => facet.values.some((value) => Boolean(value.checked)))
                .map((facet) => {
                return `${facet.id}: ${facet.values
                    .filter((value) => value.checked)
                    .map((value) => `"${value.value}"`)
                    .join(',')}`;
            })
                .join('|');
        }
        return getSelectedFacetsFromUrl(facetsParams);
    }, [facets, facetsParams]);
    const accessoryTypeTranslation = typeEnParam
        ? accessoryTypeTranslations.find((item) => item.enValue === typeEnParam)
        : undefined;
    const accessoryTypeValue = accessoryTypeTranslation?.localizedValue || accessoryTypeTranslation?.enValue;
    const hasRedirectedFilter = Boolean(typeEnParam) && Boolean(accessoryTypeValue);
    const getCommonRequestParams = useCallback(() => ({
        _br_uid_2: Cookies.get(BR_ID_COOKIE) ?? '',
        account_id: accountId,
        q: categoryId,
        ref_url: location.href,
        request_type: RequestType.SEARCH,
        search_type: SearchType.CATEGORY,
        url: location.href,
        domain_key: productCatalog,
        fl: PRODUCT_FL_PARAMS.join(','),
        fq: `${FacetFields.PRIMARY_TYPE}: "${lowerCase(productType)}"`,
    }), [accountId, categoryId, productCatalog, productType]);
    const fetchFacets = useCallback(async () => {
        try {
            const params = getCommonRequestParams();
            const mergedFacetsParams = {
                ...facetsParams,
            };
            mergedFacetsParams[FacetFields.IS_DISCOUNTED] = isDiscountedParam;
            const answer = await getFilteredProductsBloomreach({
                endpoint: searchHost,
                config: {
                    ...params,
                    start: 0,
                    rows: 0,
                    sort: sortOrder,
                    categoryId,
                    accessoryTypeTranslations,
                    accessoryUsageTranslations,
                    genderTranslations,
                    subcategoryTranslations,
                    analyticsFacets: mergedFacetsParams,
                },
            });
            const currentFacets = getCurrentFacets(answer.facet_counts.facets, filterFacetsConfig);
            const preparedFacetsData = getFacetsData({
                facetsData: currentFacets,
                facetsConfig,
                dictionary,
            });
            setHasDiscountedProducts(getHasDiscountedProducts(answer.facet_counts.facets));
            setFacets(getFacetsValues(preparedFacetsData, facetsParams));
            setIsFacetsLoaded(true);
        }
        catch (err) {
            setError(err);
            setIsLoaded(true);
        }
    }, [
        isDiscountedParam,
        accessoryTypeTranslations,
        subcategoryTranslations,
        accessoryUsageTranslations,
        categoryId,
        dictionary,
        facetsConfig,
        facetsParams,
        filterFacetsConfig,
        genderTranslations,
        getCommonRequestParams,
        searchHost,
        sortOrder,
    ]);
    const setPixelView = useCallback(() => {
        if (productCatalog && categoryId && !isInAuthor) {
            setPixelPageView(PixelPageView.CATEGORY, {
                cat_id: categoryId,
                cat: navigationBreadCrumbs.join('|'),
                domain_key: productCatalog,
            });
        }
    }, [categoryId, isInAuthor, navigationBreadCrumbs, productCatalog]);
    const fetchProducts = useCallback(async (initialOffset = 0, limit = 0) => {
        if (categoryId && accountId && productCatalog && !typeEnParam) {
            try {
                const params = getCommonRequestParams();
                setIsLoading(true);
                setOffset(initialOffset);
                const performRequest = async (offset, limit) => {
                    const mergedFacetsParams = {
                        ...facetsParams,
                    };
                    mergedFacetsParams[FacetFields.IS_DISCOUNTED] = isDiscountedParam;
                    const answer = await getFilteredProductsBloomreach({
                        endpoint: searchHost,
                        config: {
                            ...params,
                            search_type: searchParam ? SearchType.KEYWORD : SearchType.CATEGORY,
                            q: searchParam ? searchParam : categoryId,
                            fq: getFacetProperties(categoryId, mergedFacetsParams, productType, Boolean(isDiscountFromAEM), searchParam, compatibleProductKey),
                            sort: sortOrder,
                            start: offset,
                            rows: limit,
                            categoryId,
                            accessoryTypeTranslations,
                            subcategoryTranslations,
                            accessoryUsageTranslations,
                            genderTranslations,
                            analyticsFacets: mergedFacetsParams,
                        },
                    });
                    const { response: { docs, numFound }, facet_counts, autoCorrectQuery, did_you_mean, } = answer;
                    const currentFacets = getCurrentFacets(facet_counts.facets, filterFacetsConfig);
                    if (autoCorrectQuery || autoCorrectQueryResult) {
                        setAutoCorrectQueryResult(autoCorrectQuery);
                    }
                    if (did_you_mean) {
                        setDidYouMeanOptions(did_you_mean);
                    }
                    setResultsData((prevResultsData) => (offset ? [...prevResultsData, ...docs] : docs));
                    setFacets((prevFacets) => mergeProductFilterFacetsCounts(prevFacets, currentFacets));
                    setTotal(numFound);
                    // todo: md: try to remove them
                    setIsDataExists(true);
                    setIsFilterCleared(false);
                };
                if (limit > MAX_LIMIT_PER_REQUEST) {
                    let remainingLimit = limit;
                    let currentOffset = initialOffset;
                    while (remainingLimit > 0) {
                        const currentLimit = Math.min(remainingLimit, MAX_LIMIT_PER_REQUEST);
                        await performRequest(currentOffset, currentLimit);
                        currentOffset += currentLimit;
                        remainingLimit -= currentLimit;
                    }
                }
                else {
                    await performRequest(initialOffset, limit);
                }
                setIsLoaded(true);
                setPixelView();
            }
            catch (err) {
                setError(err);
            }
            finally {
                setIsLoading(false);
                setIsLoaded(true);
            }
        }
    }, [
        categoryId,
        accountId,
        productCatalog,
        typeEnParam,
        getCommonRequestParams,
        setPixelView,
        facetsParams,
        isDiscountedParam,
        searchHost,
        searchParam,
        productType,
        isDiscountFromAEM,
        compatibleProductKey,
        sortOrder,
        accessoryTypeTranslations,
        subcategoryTranslations,
        accessoryUsageTranslations,
        genderTranslations,
        filterFacetsConfig,
        autoCorrectQueryResult,
    ]);
    useEffect(() => {
        if (!isFacetsLoaded && accountId && productCatalog && !hasRedirectedFilter) {
            fetchFacets();
        }
    }, [accountId, fetchFacets, isFacetsLoaded, productCatalog, hasRedirectedFilter]);
    // general for filter hook
    useEffect(() => {
        setFacets((prev) => getFacetsValues(prev, facetsParams));
        setIsShowOnlyDiscounted(Boolean(isDiscountedParam) || Boolean(isDiscountFromAEM));
        setSearch(searchParam ?? '');
        setSortOrder(sortOrderValue);
    }, [isDiscountedParam, facetsParams, isDiscountFromAEM, searchParam, sortOrderValue]);
    useEffect(() => {
        const hasFilters = Object.values(facetsParams).some(Boolean) || (isShowOnlyDiscounted && !isDiscountFromAEM);
        if (isFilterCleared && hasFilters) {
            setIsFilterCleared(false);
        }
        if (!isFilterCleared && !hasFilters) {
            setIsFilterCleared(true);
        }
    }, [facetsParams, isDiscountFromAEM, isDiscountMode, isFilterCleared, isShowOnlyDiscounted]);
    useEffect(() => {
        if (accessoryTypeValue) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.delete(FacetFields.TYPE_EN);
            queryParams.set(FacetFields.TYPE, accessoryTypeValue);
            history.replace({
                search: queryParams.toString(),
            });
        }
    }, [accessoryTypeTranslations, accessoryTypeValue, history]);
    const handleSortOrderChange = (value) => {
        if (value !== sortOrder) {
            const queryParams = new URLSearchParams(history.location.search);
            queryParams.set(QueryParams.SORT, value);
            history.push({
                search: queryParams.toString(),
            });
        }
    };
    const onSetDiscountHandler = useCallback(() => {
        const queryParams = new URLSearchParams(history.location.search);
        if (!isShowOnlyDiscounted) {
            setIsShowOnlyDiscounted(true);
            queryParams.set(FacetFields.IS_DISCOUNTED, '1');
        }
        else {
            setIsShowOnlyDiscounted(false);
            queryParams.delete(FacetFields.IS_DISCOUNTED);
        }
        history.push({
            search: queryParams.toString(),
        });
    }, [history, isShowOnlyDiscounted]);
    const handleFilterCleared = () => {
        const queryParams = new URLSearchParams(history.location.search);
        facetsConfig.forEach((facetId) => {
            queryParams.delete(facetId);
        });
        history.push({
            search: queryParams.toString(),
        });
        setIsFilterCleared(true);
        if (isShowOnlyDiscounted && !isDiscountFromAEM) {
            onSetDiscountHandler();
        }
        setFacets((prev) => prev.map((facet) => ({
            ...facet,
            values: facet.values.map((value) => ({
                ...value,
                checked: false,
            })),
        })));
    };
    const handleSearch = (word) => {
        const queryParams = new URLSearchParams(history.location.search);
        if (word.length) {
            queryParams.set(QueryParams.SEARCH, word);
        }
        else {
            queryParams.delete(QueryParams.SEARCH);
        }
        if (!isInAuthor && word.length) {
            setPixelEvent(PixelEvent.SEARCH, {
                q: word,
                domain_key: productCatalog,
            });
        }
        history.push({
            search: queryParams.toString(),
        });
    };
    const handleUpdateFacets = useCallback((id) => (options) => {
        const currentFacet = facets.find((facet) => facet.id === id);
        if (currentFacet) {
            currentFacet.values = currentFacet.values.map((value) => ({
                ...value,
                checked: options?.find((option) => option.value === value.value)?.checked ?? false,
            }));
            const queryParams = new URLSearchParams(history.location.search);
            if (currentFacet.values.some((value) => value.checked)) {
                queryParams.set(currentFacet.id, currentFacet.values
                    .filter((value) => value.checked)
                    .map((value) => value.value)
                    .join(','));
            }
            else {
                queryParams.delete(currentFacet.id);
            }
            history.push({
                search: queryParams.toString(),
            });
        }
    }, [facets, history]);
    // Prepared Pills data
    const singlePill = useMemo(() => ({
        groupId: VARIANTS_PRICES_DISCOUNT,
        label: t('productsGrid_discounted_toggle'),
        value: isShowOnlyDiscounted,
        handleDelete: () => {
            onSetDiscountHandler();
        },
    }), [isShowOnlyDiscounted, onSetDiscountHandler, t]);
    const multiplePills = selectedFacets
        ? getPillsOptions(selectedFacets, dictionary, handleUpdateFacets, facets)
        : [];
    const isHiddenDiscountPills = isDiscountFromAEM ? false : isShowOnlyDiscounted;
    const isPillsExist = isHiddenDiscountPills || multiplePills?.some((item) => Boolean(item));
    const filteredFacets = useMemo(() => {
        return facets.map((facet) => {
            const { values, ...facetProps } = facet;
            return {
                ...facetProps,
                values: values.filter((value) => value.count > 0 || value.checked),
            };
        });
    }, [facets]);
    return {
        // common request props
        data: resultsData,
        autoCorrectQueryResult,
        setAutoCorrectQueryResult,
        didYouMeanOptions,
        isDataExists,
        error,
        isLoaded,
        isLoading,
        fetchProducts,
        offset,
        setOffset,
        isFacetsLoaded,
        hasDiscountedProducts,
        // sorting order
        sortingProps: {
            sortOrder,
            sortFilterOptions,
            handleSortOrderChange,
        },
        // clear filter
        filterClearedProps: {
            isFilterCleared,
            handleFilterCleared,
        },
        // counter props
        total,
        // search props
        searchInput: {
            searchInputValue: search,
            handleSearch,
        },
        // discounted api
        discountedApi: {
            isShowOnlyDiscounted,
            onSetDiscountHandler,
            isDiscountFromAEM,
        },
        // facets api
        facetsProps: {
            facets: filteredFacets,
            handleUpdateFacets,
        },
        pills: {
            isPillsExist,
            singlePill: isDiscountFromAEM ? undefined : singlePill,
            multiplePills,
        },
    };
}
